import { TFunction } from 'i18next';
import { ControllerExceptionCodes } from 'utils/controllerException';

// Mirrors the API's project constants (REDLAB.ISOPlanner.Core/Services/ProjectManager, such as ProjectApplyDecisions
// and ProjectRoomKeys, and the DTOs in REDLAB.ISOPlanner.Core/Data/DTOs/Projects). String families use literal
// unions plus `parseX`; unrecognized wire values map to undefined and render neutral copy, not raw strings.

// projectRoomKey: server registry string and `/rooms/{roomKey}` segment. Entity type is `kind`.

/** A room's unique key, as the server declares it. Open: a package may ship new rooms. */
export type ProjectRoomKey = string;

// Milestones and steps use platform task ids only; no key family here.

// projectState

export enum ProjectState {
  active = 0,
  completed = 1,
  archived = 9,
}

// projectTier / projectRoomAvailability (literal unions; no wire parser)
export type ProjectTier = 'trial' | 'purchased' | 'purchasedWithSharePoint';

export type ProjectRoomAvailability = 'live' | 'notSeeded';

// projectRoomDecision: `editDescription` is not a route alone (batch 400); use with
// `editedDescription` (`keep` plus edit). `setAside` requires a reason. `approve`
// approves a document on the Directie holder's approval instance, by that holder or recorded
// for them by the Security Officer. Roles: `appoint` adds the appointee to the role's holders,
// `unappoint` takes that person out of the role; both carry `appointeeUserId`. Asset inquiry: `send` creates the role's
// inquiry task for its one holder, once per role and project; it names nobody. Asset answers: `keep`,
// `adjust` (with `name`, and `containerId` to move the line) and `notInUse` (with `archiveReason`) answer a line; `add`
// (with `name` and `containerId`, item id the client's own key for the line) adds one; `remove` takes back a line the
// person added; `confirmGroup` (item id `group:{taskId}:{containerId or 0}`, no reason) answers keep on the open lines of
// a group of the list. Asset register: `assignRole` (with `ownerRoleId`) gives a line its owner role;
// `adjust` renames or moves a line; `merge` (with `memberItemIds`, the other lines, and optionally `name`, `containerId`,
// `ownerRoleId` and `proposalId`) merges 2 to 10 lines into one, the server picking the line that stays; `setAside` (with
// `archiveReason`) sets a line out of use; `restore` (Terugzetten) brings a line out of use back; `keepApart` and
// `undoKeepApart` (item id `duplicates:{proposalId}`) rule a proposal of the duplicate search different bedrijfsmiddelen,
// and take that back; `unmerge` (Toch apart houden) on a merged line takes its merge back.
// Outcome answers: `answer` (with
// `answer`: alreadySo, notYet or unsure), `clear`, `chooseDocument` (adopt |
// discuss, discuss with `reason`), `attachEvidence` and `detachEvidence` (item id `evidence:{questionItemId}:{linkId}`,
// the question's item id `{controlId}:{code}`: a library item as evidence for the outcome, or taken off it). Inquiry progress: `reassign` (item id
// `task:{taskId}`, with `appointeeUserId`: the open inquiry
// goes to that person; someone outside the role gets the role too). A send room's `send` carries `appointeeUserId` for
// a role with more than one holder: the holder who gets the inquiry. Both answers rooms: `complete` on item id
// `task:{taskId}`: the inquiry completed.

const projectRoomDecisions = [
  'keep',
  'setAside',
  'editDescription',
  'appoint',
  'unappoint',
  'approve',
  'send',
  'adjust',
  'notInUse',
  'restore',
  'add',
  'remove',
  'confirmGroup',
  'assignRole',
  'merge',
  'keepApart',
  'undoKeepApart',
  'unmerge',
  'answer',
  'clear',
  'chooseDocument',
  'clearDocument',
  'reassign',
  'attachEvidence',
  'detachEvidence',
  'complete',
  'quoteRequest',
  'quoteRequested',
  'quoteSigned',
  'arrangedElsewhere',
  'dates',
  'skipAudit',
  'undo',
] as const;
export type ProjectDecisionWord = (typeof projectRoomDecisions)[number];

/** Returns undefined for unrecognized values; callers render neutral copy, not the raw string. */
export const parseProjectDecisionWord = (value: string | undefined): ProjectDecisionWord | undefined => {
  if (value === undefined) return undefined;

  return projectRoomDecisions.find((word) => word === value);
};

// The item ids the server's rooms read back for a decision that is not about a row of the read: a message mark is
// `task:{taskId}` (inquiry progress), the confirmation of a group of a list is `group:{taskId}:{containerId}` (asset
// answers, 0 for the group without a container). The prefixes are declared once here, next to the decision words they
// go with.

const projectInquiryTaskItemPrefix = 'task:';
const projectGroupItemPrefix = 'group:';

/** The item id of `reassign` and `complete` on an inquiry: `task:{taskId}`. */
export const getProjectInquiryTaskItemId = (taskId: number): string => projectInquiryTaskItemPrefix + taskId;

/** The item id of `confirmGroup` in the asset answers: a group of the list of an inquiry task. */
export const getProjectGroupItemId = (taskId: number, containerId: number | undefined): string =>
  `${projectGroupItemPrefix}${taskId}:${containerId ?? 0}`;

// projectWriteBlocker: why the caller cannot write a room or a project now, the server's write gate. `noPermission` = a
// right the write needs is missing; `archived` = the project is archived; `trialEnded` = the trial ended unbought;
// `confirmed` = the room is confirmed (a room read only).

const projectWriteBlockers = ['noPermission', 'archived', 'trialEnded', 'confirmed'] as const;
export type ProjectWriteBlocker = (typeof projectWriteBlockers)[number];

/**
 * Narrows a wire string to a write blocker: undefined when absent (the write is taken); noPermission for one this client
 * cannot name, so nothing is offered to write.
 */
export const parseProjectWriteBlocker = (value: string | undefined | null): ProjectWriteBlocker | undefined =>
  value === undefined || value === null
    ? undefined
    : projectWriteBlockers.find((word) => word === value) ?? 'noPermission';

// projectMilestoneSwitchBlocker: why a milestone does not switch now, the server's rule: `closed`, `notCurrent` (not the
// journey's current milestone), or a projectWriteBlocker (the project's write gate, Update on the milestone task).

const projectMilestoneSwitchBlockers = ['closed', 'notCurrent', 'noPermission', 'archived', 'trialEnded'] as const;
export type ProjectMilestoneSwitchBlocker = (typeof projectMilestoneSwitchBlockers)[number];

/**
 * Narrows a wire string to a switch blocker: undefined when absent (the switch is taken); notCurrent for one this client
 * cannot name, so no switch is offered.
 */
export const parseProjectMilestoneSwitchBlocker = (
  value: string | undefined | null,
): ProjectMilestoneSwitchBlocker | undefined =>
  value === undefined || value === null
    ? undefined
    : projectMilestoneSwitchBlockers.find((word) => word === value) ?? 'notCurrent';

// projectInquiryListMode: what the caller may do on an asset inquiry's list, by the server's apply rule. `full` = every
// decision; `addOnly` = the register is approved, only a late addition; `readOnly` = no write.

const projectInquiryListModes = ['full', 'addOnly', 'readOnly'] as const;
export type ProjectInquiryListMode = (typeof projectInquiryListModes)[number];

/** Narrows a wire string to a list mode; readOnly for an absent or unrecognized one, so nothing is offered to write. */
export const parseProjectInquiryListMode = (value: string | undefined | null): ProjectInquiryListMode =>
  projectInquiryListModes.find((word) => word === value) ?? 'readOnly';

// projectInquiryGroupStatus: where a group of an asset inquiry's list stands for the caller, the server's rule. `none` =
// nothing to confirm (the group asks nothing, or the list does not take every decision); `toConfirm` = a line or the
// role's own container still waits for an answer; `done` = everything answered.

const projectInquiryGroupStatuses = ['none', 'toConfirm', 'done'] as const;
export type ProjectInquiryGroupStatus = (typeof projectInquiryGroupStatuses)[number];

/** Narrows a wire string to a group status; none for an absent or unrecognized one. */
export const parseProjectInquiryGroupStatus = (value: string | undefined | null): ProjectInquiryGroupStatus =>
  projectInquiryGroupStatuses.find((word) => word === value) ?? 'none';

// projectRoomHolderLicenceState: whether a role holder can take the role's work. `ok` = a working
// login with a management licence; `noLogin` = no working login (no licence, deleted, or an
// application account); `noManagementLicence` = can log in, but holds neither Admin nor Manager.

/** The wire values a role holder's licence state can carry, in the API's declared order. */
const projectRoomHolderLicenceStates = ['ok', 'noLogin', 'noManagementLicence'] as const;
export type ProjectRoomHolderLicenceState = (typeof projectRoomHolderLicenceStates)[number];

/** Narrows a wire string to a licence state; undefined for an absent or unrecognized one. */
export const parseProjectRoomHolderLicenceState = (
  value: string | undefined,
): ProjectRoomHolderLicenceState | undefined => {
  if (value === undefined) return undefined;

  return projectRoomHolderLicenceStates.find((word) => word === value);
};

// projectInquiryStatus: the state of the inquiry a role was sent, derived by the server from the
// role's inquiry task on every read. `notSent` = no task; `answered` = completed; `overdue` = past
// its deadline; `opened` = accepted or started; `sent` = otherwise.

/** The wire values an inquiry status can carry, in the API's declared order. */
export const projectInquiryStatuses = ['notSent', 'sent', 'opened', 'answered', 'overdue'] as const;
export type ProjectInquiryStatus = (typeof projectInquiryStatuses)[number];

/** Narrows a wire string to an inquiry status; undefined for an absent or unrecognized one. */
export const parseProjectInquiryStatus = (value: string | undefined | null): ProjectInquiryStatus | undefined => {
  if (value === undefined || value === null) return undefined;

  return projectInquiryStatuses.find((word) => word === value);
};

// projectInquiryBlocker: why the asset inquiry cannot be sent to a role that has none, in the order
// the send refuses: nobody holds it (PROJECT0063), more than one person holds it (PROJECT0038), then
// its one holder cannot take the work (PROJECT0064).

/** The wire values an inquiry blocker can carry, in the API's declared order. */
export const projectInquiryBlockers = ['noResponsible', 'severalHolders', 'noActiveLicence'] as const;
export type ProjectInquiryBlocker = (typeof projectInquiryBlockers)[number];

/** Narrows a wire string to an inquiry blocker; undefined for an absent or unrecognized one. */
export const parseProjectInquiryBlocker = (value: string | undefined | null): ProjectInquiryBlocker | undefined => {
  return projectInquiryBlockers.find((word) => word === value);
};

/** Why an asset line goes out of use (archiveReason): Hebben we niet, Gebruiken we niet meer. */
export const projectAssetArchiveReasons = ['notApplicable', 'notInUse'] as const;
export type ProjectAssetArchiveReason = (typeof projectAssetArchiveReasons)[number];

// projectAssetAnswer: the answer an asset inquiry stored on a line: in use as it is (`keep`), in use under another name
// or in another container (`adjust`), not in use (`notInUse`), or added by the responsible person (`added`).

/** The wire values an asset line's answer can carry, in the API's declared order. */
export const projectAssetAnswers = ['keep', 'adjust', 'notInUse', 'added'] as const;
export type ProjectAssetAnswer = (typeof projectAssetAnswers)[number];

// projectRegisterApproveBlocker: why the asset register cannot be approved now, in the API's order: it holds no asset,
// an asset is still to assign, an asset added without a group still has no container, a role that owns assets has no
// inquiry sent, or a sent inquiry is still open before its deadline.

/** The wire values a register approve blocker can carry, in the API's declared order. */
export const projectRegisterApproveBlockers = [
  'noAssets',
  'toAssign',
  'toGroup',
  'inquiriesNotSent',
  'inquiriesOpen',
] as const;
export type ProjectRegisterApproveBlocker = (typeof projectRegisterApproveBlockers)[number];

// The asset register's duplicate search: why it failed, and the status of a proposal. Why a merge, a keepApart, an
// unmerge or a restore was refused with PROJECT0049 is the apply row's refusalReason.

export const projectAssetDuplicateFailures = ['tooMany', 'modelFailed', 'timeout', 'creditRefused'] as const;
export type ProjectAssetDuplicateFailureReason = (typeof projectAssetDuplicateFailures)[number];

export const projectAssetDuplicateProposalStatuses = ['open', 'merged', 'keptApart'] as const;
export type ProjectAssetDuplicateProposalStatus = (typeof projectAssetDuplicateProposalStatuses)[number];

export const projectRegisterMergeRefusals = [
  'notInUse',
  'keptApart',
  'nested',
  'stale',
  'severalLinked',
  'merged',
] as const;
export type ProjectRegisterMergeRefusal = (typeof projectRegisterMergeRefusals)[number];

// What went wrong after an applied or already-applied write that stands: the apply row's warning. publishFailed: the
// documents room approved the document, but its publication failed.
export const projectApplyItemWarnings = ['publishFailed'] as const;
export type ProjectApplyItemWarning = (typeof projectApplyItemWarnings)[number];

// projectErrorCodes: PROJECT* subset of ControllerExceptionCodes.

export type ProjectErrorCode = ControllerExceptionCodes;

export const projectErrorCodes: readonly ProjectErrorCode[] = Object.values(ControllerExceptionCodes).filter((code) =>
  code.startsWith('PROJECT'),
);

// Track families: wire values from ProjectTrackForecast; parse to undefined when unrecognized.

/** The three states one project milestone can be in, in the API's declared order. */
const projectMilestoneStates = ['todo', 'inProgress', 'done'] as const;
export type ProjectMilestoneState = (typeof projectMilestoneStates)[number];

/** Narrows a wire string to a milestone state; undefined for an absent or unrecognized one. */
export const parseProjectMilestoneState = (value: string | undefined): ProjectMilestoneState | undefined => {
  if (value === undefined) return undefined;

  return projectMilestoneStates.find((word) => word === value);
};

/** What the track says about the whole project, in the API's declared order. */
const projectTrackStates = ['onTrack', 'atRisk', 'stalled', 'behind', 'notStarted', 'completed', 'archived'] as const;
export type ProjectTrackState = (typeof projectTrackStates)[number];

/** Narrows a wire string to a track state; undefined for an absent or unrecognized one. */
export const parseProjectTrackState = (value: string | undefined): ProjectTrackState | undefined => {
  if (value === undefined) return undefined;

  return projectTrackStates.find((word) => word === value);
};

/**
 * Where the forecast came from: this project's own pace, the baseline (nothing has completed yet),
 * or nowhere at all. It decides whether a date may be called a forecast.
 */
const projectTrackForecastKinds = ['forecast', 'typical', 'none'] as const;
export type ProjectTrackForecastKind = (typeof projectTrackForecastKinds)[number];

/** Narrows a wire string to a forecast kind; undefined for an absent or unrecognized one. */
export const parseProjectTrackForecastKind = (value: string | undefined): ProjectTrackForecastKind | undefined => {
  if (value === undefined) return undefined;

  return projectTrackForecastKinds.find((word) => word === value);
};

/** What one band of the timeline stands for, in the API's declared order. */
const projectTrackSegmentKinds = ['actual', 'current', 'projected', 'todo'] as const;
export type ProjectTrackSegmentKind = (typeof projectTrackSegmentKinds)[number];

/** Narrows a wire string to a segment kind; undefined for an absent or unrecognized one. */
export const parseProjectTrackSegmentKind = (value: string | undefined): ProjectTrackSegmentKind | undefined => {
  if (value === undefined) return undefined;

  return projectTrackSegmentKinds.find((word) => word === value);
};

/** The severity of the track's message, in the API's declared order. */
const projectTrackMessageTones = ['amber', 'red', 'info', 'quiet'] as const;
export type ProjectTrackMessageTone = (typeof projectTrackMessageTones)[number];

/** Narrows a wire string to a message tone; undefined for an absent or unrecognized one. */
export const parseProjectTrackMessageTone = (value: string | undefined): ProjectTrackMessageTone | undefined => {
  if (value === undefined) return undefined;

  return projectTrackMessageTones.find((word) => word === value);
};

/** Which message the track states, in the API's declared order. */
const projectTrackMessageCodes = [
  'behindTarget',
  'targetPassed',
  'atRisk',
  'stalled',
  'noTarget',
  'notStarted',
  'nothingDoneYet',
  'ahead',
  'onTrack',
  'completed',
  'archived',
] as const;
export type ProjectTrackMessageCode = (typeof projectTrackMessageCodes)[number];

/** Narrows a wire string to a message code; undefined for an absent or unrecognized one. */
export const parseProjectTrackMessageCode = (value: string | undefined): ProjectTrackMessageCode | undefined => {
  if (value === undefined) return undefined;

  return projectTrackMessageCodes.find((word) => word === value);
};

/** The moves the track can put forward, in the API's declared order. */
const projectTrackMoveKinds = [
  'completeStageTask',
  'markStageDone',
  'openRoom',
  'reviewTarget',
  'setTarget',
  'setUpStageTasks',
] as const;
export type ProjectTrackMoveKind = (typeof projectTrackMoveKinds)[number];

/** Narrows a wire string to a move kind; undefined for an absent or unrecognized one. */
export const parseProjectTrackMoveKind = (value: string | undefined): ProjectTrackMoveKind | undefined => {
  if (value === undefined) return undefined;

  return projectTrackMoveKinds.find((word) => word === value);
};

/** How a target date compares with the forecast, in the API's declared order. */
const projectTargetFits = ['comfortable', 'onPlan', 'ambitious', 'veryAmbitious', 'none'] as const;
export type ProjectTargetFit = (typeof projectTargetFits)[number];

/** Narrows a wire string to a target fit; undefined for an absent or unrecognized one. */
export const parseProjectTargetFit = (value: string | undefined): ProjectTargetFit | undefined => {
  if (value === undefined) return undefined;

  return projectTargetFits.find((word) => word === value);
};

// ---------------------------------------------------------------------------
// projectSeedOutcome / projectSeedItemKind
//
// The two closed value sets of one POST projects/seed/{tenantId} result row (Tenant Management:
// "Seed for Kickstart"). Same rule as every family above: an unrecognized wire value resolves to
// undefined, never rendered raw.
// ---------------------------------------------------------------------------

/** What the seed call did with one milestone, room anchor or step. */
export const projectSeedOutcomes = ['created', 'alreadyExisted', 'skipped', 'failed', 'deleted'] as const;
export type ProjectSeedOutcome = (typeof projectSeedOutcomes)[number];

/** Narrows a wire string to a seed outcome; undefined for an absent or unrecognized one. */
export const parseProjectSeedOutcome = (value: string | undefined): ProjectSeedOutcome | undefined => {
  if (value === undefined) return undefined;

  return projectSeedOutcomes.find((word) => word === value);
};

/** What one seed result row is about: a node of the tree, a role, or a document linked to a step. */
export const projectSeedItemKinds = [
  'milestone',
  'anchor',
  'followUp',
  'step',
  'task',
  'role',
  'document',
] as const;
export type ProjectSeedItemKind = (typeof projectSeedItemKinds)[number];

/** Narrows a wire string to a seed item kind; undefined for an absent or unrecognized one. */
export const parseProjectSeedItemKind = (value: string | undefined): ProjectSeedItemKind | undefined => {
  if (value === undefined) return undefined;

  return projectSeedItemKinds.find((word) => word === value);
};

// ---------------------------------------------------------------------------
// insightSource / insightCitationKind / insightActionKind / insightTargetPanel
//
// The closed word sets of one AIInsight (Contracts/AI/DataModels/Insight). Same rule as every
// family above: an unrecognized wire value resolves to undefined, and the item carrying it is
// dropped rather than rendered raw - a citation kind decides the whole chip, an action kind the
// whole button.
// ---------------------------------------------------------------------------

/** Where an insight came from, in the API's declared order. */
const insightSources = ['ai', 'unavailable', 'notApplicable'] as const;
export type InsightSource = (typeof insightSources)[number];

/** Narrows a wire string to an insight source; undefined for an absent or unrecognized one. */
export const parseInsightSource = (value: string | undefined): InsightSource | undefined => {
  if (value === undefined) return undefined;

  return insightSources.find((word) => word === value);
};

/** What one citation points at, in the API's declared order. */
const insightCitationKinds = ['deliverable', 'document', 'clause', 'entity'] as const;
export type InsightCitationKind = (typeof insightCitationKinds)[number];

/** Narrows a wire string to a citation kind; undefined for an absent or unrecognized one. */
export const parseInsightCitationKind = (value: string | undefined): InsightCitationKind | undefined => {
  if (value === undefined) return undefined;

  return insightCitationKinds.find((word) => word === value);
};

/** What one action does, in the API's declared order. */
const insightActionKinds = ['navigate', 'openPanel', 'run'] as const;
export type InsightActionKind = (typeof insightActionKinds)[number];

/** Narrows a wire string to an action kind; undefined for an absent or unrecognized one. */
export const parseInsightActionKind = (value: string | undefined): InsightActionKind | undefined => {
  if (value === undefined) return undefined;

  return insightActionKinds.find((word) => word === value);
};

/** The panels an action can open, in the API's declared order. */
const insightTargetPanels = ['targetDate'] as const;
export type InsightTargetPanel = (typeof insightTargetPanels)[number];

/** Narrows a wire string to a panel; undefined for an absent or unrecognized one. */
export const parseInsightTargetPanel = (value: string | undefined): InsightTargetPanel | undefined => {
  if (value === undefined) return undefined;

  return insightTargetPanels.find((word) => word === value);
};

// ---------------------------------------------------------------------------
// documentSummarySource
//
// The closed word set of one AIDocumentSummary (Contracts/AI/DataModels/Documents). Same rule as
// every family above: an unrecognized wire value resolves to undefined, and the entry carrying it
// is dropped rather than rendered raw - the source decides whether the row shows two lines or the
// one "not available" line.
// ---------------------------------------------------------------------------

/** Where a document's two lines came from, in the API's declared order. */
const documentSummarySources = ['ai', 'unavailable'] as const;
export type DocumentSummarySource = (typeof documentSummarySources)[number];

/** Narrows a wire string to a summary source; undefined for an absent or unrecognized one. */
export const parseDocumentSummarySource = (value: string | undefined): DocumentSummarySource | undefined => {
  if (value === undefined) return undefined;

  return documentSummarySources.find((word) => word === value);
};

// i18n mappers: literal keys only; unknown values use General.NotAvailable or family neutral text.

export const getProjectNeutralText = (t: TFunction<string[]>): string => {
  return t('projects:General.NotAvailable');
};

/**
 * `isCompleted` drives the completed label, not `ProjectState.completed`. Archived state takes
 * precedence over completed.
 */
export const getProjectStateLabel = (
  t: TFunction<string[]>,
  state: ProjectState | undefined,
  isCompleted: boolean,
): string => {
  if (state === ProjectState.archived) return t('projects:State.Archived');
  if (isCompleted || state === ProjectState.completed) return t('projects:State.Completed');
  if (state === ProjectState.active) return t('projects:State.Active');

  return getProjectNeutralText(t);
};

export const getTierCopy = (t: TFunction<string[]>, tier: ProjectTier | string | undefined): string => {
  switch (tier) {
    case 'trial':
      return t('projects:Tier.trial');
    case 'purchased':
      return t('projects:Tier.purchased');
    case 'purchasedWithSharePoint':
      return t('projects:Tier.purchasedWithSharePoint');
    default:
      return getProjectNeutralText(t);
  }
};

export const getAvailabilityLabel = (
  t: TFunction<string[]>,
  availability: ProjectRoomAvailability | string | undefined,
): string => {
  switch (availability) {
    case 'live':
      return t('projects:Availability.Ready');
    case 'notSeeded':
      return t('projects:Availability.NotSeeded');
    default:
      return t('projects:Availability.NotAvailable');
  }
};

export const getMilestoneStateLabel = (
  t: TFunction<string[]>,
  state: ProjectMilestoneState | string | undefined,
): string => {
  switch (state) {
    case 'todo':
      return t('projects:Milestone.State.todo');
    case 'inProgress':
      return t('projects:Milestone.State.inProgress');
    case 'done':
      return t('projects:Milestone.State.done');
    default:
      return getProjectNeutralText(t);
  }
};
